import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, computed, onBeforeUpdate, PropType, reactive } from "vue";
import Highcharts from "highcharts";
import { Chart } from "highcharts-vue";
import TooltipFormatter from "@/guiide/ts/tooltipFormatter";
import ChartSeries from "@/guiide/ts/chartSeries";
import "highcharts/modules/accessibility";
import { HighchartsChartOptions } from "@/guiide/ts/highchartsChartOptions";
import { SeriesColour } from "@/guiide/ts/charts/seriesColour";
import { HighchartsIncomeChartSeries } from "@/guiide/ts/response/charts/highchartsIncomeChartSeries";
import { IncomeSourceChartSeries } from "@/guiide/ts/response/charts/incomeSourceChartSeries";
import ChartLegend from "./legend/ChartLegend.vue";
import { LegendItem } from "@/guiide/ts/response/charts/types";
import { getLegendItemsFromIncomeChartSeries } from "@/guiide/ts/response/charts/chartUtils";
import Constants from "@/guiide/ts/constants";

    
export default /*@__PURE__*/_defineComponent({
  __name: 'IncomeChart',
  props: {
        chartData: { type: Array<HighchartsIncomeChartSeries>, required: true },
        detailChartData: { type: Array<IncomeSourceChartSeries>, required: false },
        chartOptions: { type: Object as PropType<HighchartsChartOptions>, required: true },
        showLegend: { type: Boolean, required: false, default: true },
        useChartDataLegendIndex: { type: Boolean, required: false, default: true },
        highlightOnLegendItemFocus: { type: Boolean, required: false, default: false }
    },
  setup(__props) {

const props = __props;

    const incomeChart = ref<{ chart: Highcharts.Chart } | null>(null);
    const detailChart = ref(null);
    const legendItemVisibility = reactive<Record<string, boolean>>({});
    const legendDisabled = ref(false);
    let legendBlurDebounceTimer: number | null = null;

    onBeforeUpdate(() => {
        incomeChart.value.chart.reflow();
        detailChart.value.chart.reflow();
    });

    const showMainChart = ref(true);
    const showDetailChart = computed<boolean>(() => !showMainChart.value);
    const detailChartYear = ref(1);

    const getDetailChartData = computed<object>(() => {
        const detailData = props.detailChartData
            .filter(aid => aid.year == detailChartYear.value)
            .map(d => {
                const color = SeriesColour.getColour(d.class);

                return {
                    name: d.name,
                    y: d.value,
                    color: color
                };
            });

        return [
            {
                colorByPoint: true,
                data: detailData
            }
        ];
    });

    const incomeChartOptions = computed<object>(() => {
        return {
            accessibility: {
                enabled: true,
                description: props.chartOptions.description,
                keyboardNavigation: {
                    enabled: true,
                },
                tooltip: {
                    enabled: true,
                },
                announceNewData: {
                    enabled: true,
                    minAnnounceInterval: 2000
                },
                point: {
                    descriptionFormatter: function (point) {
                        return `Year ${point.category}, value ${point.y} thousand pounds.`;
                    }
                }
            },
            chart: {
                backgroundColor: "white",
                borderColor: "",
                borderWidth: 1,
                borderRadius: 3,
                zoomType: "x",
                panning: { enabled: false },
                type: "column"
            },
            title: {
                text: props.chartOptions.title,
                style: {
                    color: "#5c5b5b",
                    fontWeight: "600",
                    fontSize: "14px"
                }
            },
            xAxis: {
                title: {
                    text: "Retirement year",
                    style: {
                        color: "#5c5b5b",
                        fontWeight: "500",
                        fontSize: "12px"
                    }
                },
                accessibility: {
                    description: 'Each year of retirement (up to 30).'
                },
                allowDecimals: false,
                tickInterval: 1,
                tickLength: 0
            },
            yAxis: {
                title: {
                    text: null,
                    rotation: 0,
                    style: {
                        color: "#5c5b5b",
                        fontWeight: "500",
                        fontSize: "12px"
                    },
                    accessibility: {
                        description: 'Cash amount (in thousands) allocated to each year.'
                    }
                },
                labels: {
                    formatter: function() {
                        return (
                            "£" +
                            Highcharts.numberFormat(this.value / 1000, 0, ".", ",") +
                            "k"
                        );
                    }
                },
                stackLabels: {
                    enabled: false,
                    style: {
                        fontWeight: "bold",
                        color: "gray"
                    }
                }
            },
            legend: {
                enabled: props.showLegend,
                verticalAlign: "bottom" as Highcharts.VerticalAlignValue,
                floating: false,
                backgroundColor: "white",
                borderColor: "",
                borderWidth: 0,
                borderRadius: 0,
                shadow: false,
                itemMarginBottom: 2,
                itemStyle: {
                    color: "Grey",
                    fontWeight: "lighter",
                    fontSize: "12px"
                }
            },
            tooltip: {
                borderColor: "DimGrey",
                backgroundColor: "#EBF4FA",
                borderWidth: 0,
                borderRadius: 10,
                shadow: false,
                animation: true,
                shared: props.chartOptions.sharedTooltip,
                formatter: function () {
                    if (props.detailChartData) {
                        const allSeries = this.series.chart.series;
                        const allChartSeries = new Array<ChartSeries>();
                        allSeries.forEach(s => {
                            const yData = s.data.map(d => d.y);
                            const chartSeries = new ChartSeries(s.name, yData);
                            allChartSeries.push(chartSeries);
                        });
                        return TooltipFormatter.createNew(this.x, allChartSeries);
                    } else {
                        return TooltipFormatter.getTooltipForIncomeSourceBreakdown(this.x, this.points);
                    }
                },
                positioner: function(labelWidth, labelHeight, point) {
                    return {
                        x: Math.max(point.plotX - labelWidth + 45, 10),
                        y: 0
                    };
                }
            },
            plotOptions: {
                line: {
                    pointStart: 1
                },
                column: {
                    pointPadding: -0.1,
                    borderWidth: 1,
                    shadow: false,
                    borderRadius: 0,
                    stacking: 'normal' as Highcharts.OptionsStackingValue,
                    dataLabels: {
                        enabled: false,
                        color: "black"
                    },
                    pointStart: 1,
                    cursor: props.chartOptions.seriesHoverCursor,
                    point: {
                        events: props.detailChartData
                            ? { click: ({ point }) => { configureForDetailChart(point.category); } }
                            : {}
                    }
                },
                series: {
                    states: {
                        inactive: {
                            enabled: true,
                            opacity: 0.7
                        }
                    }
                }
            },
            series: props.chartData,
            credits: {
                enabled: false
            }
        };
    });

    const legendItems = computed<Array<LegendItem>>(() => {
        const legendItems = getLegendItemsFromIncomeChartSeries(props.chartData, props.useChartDataLegendIndex);

        return legendItems.map(li => ({
            ...li,
            visible: legendItemVisibility[li.key] ?? li.visible
        }));
    });

    const detailChartOptions = computed<object>(() => {
        return {
            accessibility: {
                enabled: true,
                description: 'A drilldown pie chart showing a breakdown of all income sources for the given year.',
                keyboardNavigation: {
                    enabled: true,
                },
                announceNewData: {
                    enabled: true,
                    minAnnounceInterval: 2000
                }
            },
            chart: {
                plotBackgroundColor: null,
                plotBorderWidth: null,
                plotShadow: false,
                type: "pie",
                height: null,
                events: {
                    load: function () {
                        const chart = this;

                        chart.customButton = chart.renderer.button(
                            "Back",
                            16,
                            0,
                            () => toggleNetIncomeDisplayChart(), {
                            height: 10,
                            width: 28
                        }
                        ).add();
                    }
                }
            },
            title: {
                text: "Income source in retirement year " + detailChartYear.value,
                style: {
                    color: "#5c5b5b",
                    fontWeight: "600",
                    fontSize: "14px"
                }
            },
            tooltip: {
                enabled: false
            },
            plotOptions: {
                pie: {
                    allowPointSelect: false,
                    dataLabels: {
                        enabled: true,
                        style: {
                            fontSize: "0.9em",
                            fontWeight: "lighter"
                        },
                        formatter: function() {
                            return (
                                this.point.name +
                                ":<br/>£" +
                                Highcharts.numberFormat(this.y, 0, ".", ",")
                            );
                        }
                    }
                }
            },
            series: props.detailChartData
                ? getDetailChartData.value
                : [],
            credits: {
                enabled: false
            }
        };
    });

    function toggleNetIncomeDisplayChart() {
        showMainChart.value = !showMainChart.value;
        legendDisabled.value = !showMainChart.value;
    }

    function configureForDetailChart(retirementYear: number) {
        detailChartYear.value = retirementYear;
        toggleNetIncomeDisplayChart();
    }

    function onLegendItemToggle(item: LegendItem) {

        legendItemVisibility[item.key] = !item.visible;

        const series = getSeriesById(item.key);

        if (series) {
            series.setVisible(legendItemVisibility[item.key], false);
            setSeriesState(series);
        }
    }

    function onLegendItemFocus(item: LegendItem) {

        if (!incomeChart.value) {
            return;
        }

        if (legendBlurDebounceTimer !== null) {
            window.clearTimeout(legendBlurDebounceTimer);
            legendBlurDebounceTimer = null;
        }

        const focusedSeries = getSeriesById(item.key);

        const custom = (focusedSeries.userOptions.custom ??= {});
        if (!custom.originalColor) {
            custom.originalColor = focusedSeries.color as string;
        }

        setSeriesState(focusedSeries);
    }

    function onLegendItemBlur(item: LegendItem) {

        if (!incomeChart.value) {
            return;
        }

        if (legendBlurDebounceTimer !== null) {
            window.clearTimeout(legendBlurDebounceTimer);
        }

        legendBlurDebounceTimer = window.setTimeout(() => {
            legendBlurDebounceTimer = null;
            setSeriesState(null);
        }, 0);
    }

    function setSeriesState(focusedSeries: Highcharts.Series) {

        for (const series of incomeChart.value.chart.series) {
            let state: "" | "hover" | "inactive" = series === focusedSeries ? "hover" : "inactive";

            if (!focusedSeries || !focusedSeries.visible) {
                state = "";
            }

            series.setState(state);

            if (props.highlightOnLegendItemFocus) {
                if (series === focusedSeries) {
                    focusedSeries.update({ color: Constants.Color.classicgold } as Highcharts.SeriesOptionsType, true);
                } else {
                    const originalColor = series.userOptions.custom?.originalColor;

                    if (originalColor) {
                        series.update({ color: originalColor } as Highcharts.SeriesOptionsType, false);
                    }
                }
            }
        }

        incomeChart.value.chart.redraw();
    }

    function getSeriesById(key: string): undefined|Highcharts.Series {
        return incomeChart.value.chart.get(key) as Highcharts.Series;
    }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createVNode(_unref(Chart), {
      ref_key: "incomeChart",
      ref: incomeChart,
      options: incomeChartOptions.value
    }, null, 8, ["options"]), [
      [_vShow, showMainChart.value]
    ]),
    _withDirectives(_createVNode(_unref(Chart), {
      ref_key: "detailChart",
      ref: detailChart,
      options: detailChartOptions.value
    }, null, 8, ["options"]), [
      [_vShow, showDetailChart.value]
    ]),
    _createElementVNode("div", null, [
      _createVNode(ChartLegend, {
        items: legendItems.value,
        disable: legendDisabled.value,
        class: "mt-1 mb-4 px-3",
        onItemToggle: onLegendItemToggle,
        onItemFocus: onLegendItemFocus,
        onItemBlur: onLegendItemBlur
      }, null, 8, ["items", "disable"])
    ])
  ], 64))
}
}

})